import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { MetaDataValues } from '@common/collab/types/meta-data-values.type';
import { FlareSchema } from '@common/flare/flare-schema';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ImagesFilter, SnippetFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { CollabFileTextEditorComponent } from '@portal-core/text-editor/components/collab-file-text-editor/collab-file-text-editor.component';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { LoadingState } from '@portal-core/util/loading-state';
import { Cancelable } from 'lodash';
import { Plugin } from 'prosemirror-state';
import { NodeViewConstructor } from 'prosemirror-view';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'mc-project-files-tree-item-preview',
  templateUrl: './project-files-tree-item-preview.component.html',
  styleUrls: ['./project-files-tree-item-preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectFilesTreeItemPreviewComponent implements OnInit {
  @Input() commitId: string;
  @Input() projectId: number;
  @Input() defaultPath: string;
  @Input() branchName: string;
  @Input() treeFileFilter?: string;
  // For snippet preview
  @Input() licenseUser?: LicenseUser;
  @Input() projectLanguage?: string;
  @Input() schema?: FlareSchema;
  @Input() debouncedReflowGutterLayout?: Function & Cancelable;
  @Input() metaData$?: Observable<MetaDataValues>;
  @Input() viewPluginOverlay$?: BehaviorSubject<HTMLElement>;
  @Input() editorScrollable?: CdkScrollable;
  @Input() initialFilePath?: string;
  @Input() usingWorkspaceTree?: boolean;
  // General
  ProjectFileType: typeof ProjectFileType = ProjectFileType;
  previewLoadingState: LoadingState<ErrorCode> = new LoadingState<ErrorCode>();
  previewSrc: string;
  fileType: ProjectFileType;
  fileTypeLabel: string;

  // For parent to use
  public selectedFilePath: string;
  @ViewChild('snippetPreviewEditor') snippetPreviewEditor: CollabFileTextEditorComponent;

  // For snippet preview
  protected nodeViews$: BehaviorSubject<Dictionary<NodeViewConstructor>> = new BehaviorSubject<Dictionary<NodeViewConstructor>>(null);
  protected flareFile$: BehaviorSubject<{ src: string, nodeViews: Dictionary<NodeViewConstructor>, plugins: Plugin<any>[] }> = new BehaviorSubject<{ src: string, nodeViews: Dictionary<NodeViewConstructor>, plugins: Plugin<any>[] }>(null);
  collabType: CollabFileType = CollabFileType.Workspace;

  constructor(
    private projectFilesService: ProjectFilesService,
    private textEditorService: TextEditorService
  ) { }

  ngOnInit() {
    if (this.treeFileFilter === ImagesFilter) {
      this.fileTypeLabel = 'Images';
    } else if (this.treeFileFilter === SnippetFileFilter) {
      this.fileTypeLabel = 'Snippets';
    } else {
      this.fileTypeLabel = 'Files';
    }

    if (this.initialFilePath) {
      this.onProjectFileSelected({ path: this.initialFilePath });
    }
  }

  /** Updates the preview image and enables the save button */
  onProjectFileSelected(fileNode: { path: string }) {
    const newSrc = this.projectFilesService.getProjectFileRawPath(this.projectId, this.commitId, fileNode.path);

    if (newSrc === this.previewSrc) {
      return;
    }

    this.previewLoadingState.update(true);
    this.fileType = this.projectFilesService.getFileType(fileNode.path);
    if (this.fileType === ProjectFileType.Flare) {
      // Update the src and node views with the new file path
      // Update the plugins so that the pm editor creates a new editor view
      this.flareFile$.next({
        src: !this.usingWorkspaceTree ? newSrc : fileNode.path,
        nodeViews: this.textEditorService.createCommonNodeViews(this.schema, fileNode.path, this.projectId, this.commitId, this.debouncedReflowGutterLayout, this.metaData$, null, this.viewPluginOverlay$, this.editorScrollable, this.branchName, null, CollabFileType.Workspace, this.licenseUser, this.projectLanguage),
        plugins: this.textEditorService.createFlarePlugins(this.schema, { metaData: { metaData$: this.metaData$ } })
      });
    }

    this.selectedFilePath = fileNode.path;
    this.previewSrc = newSrc;
  }

  onPreviewLoad() {
    this.previewLoadingState.update(false);
  }

  onPreviewError(error) {
    if (error) {
      this.previewLoadingState.update(false, ErrorCode.ProjectFileNotFoundError);
    }
  }
}
